import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { listTableTitles } from 'constants.js';
import { ToolbarV2, TableComponentV2 } from 'components';
import useLists from './useLists';
import AddListModal from './components/AddListModal';
import {
  deleteList,
  getPaginatedLists,
  resetSnackbar,
  setCurrentList,
} from 'store/lists/actions';
import { actions as userMessageActions } from 'store/userMessaging';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { getEnvVar } from 'helpers';
import filterHelpers from 'store/helpers/filterHelpers';
import { updateListFilters, resetListFilters } from 'store/lists/actions';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  }
}));

const Lists = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const lists = useLists();

  const {
    openCreateListModal,
    closeListModal,
    onClickAddList,
    updateCurrentErrorValidationSegment
  } = lists;

  const {
    isTemplateReadOnly
  } = filterHelpers();

  const gettingList = useSelector(store => store.lists.subscribersList.getting);
  const listsData = useSelector(store => store.lists.subscribersList.listsData);
  const showSnackbar = useSelector(store => store.lists.showSnackbar);
  const snackbarMessage = useSelector(store => store.lists.snackbarMessage);
  const snackbarSeverity = useSelector(store => store.lists.snackbarSeverity);
  const filters = useSelector(store => store.lists.filters);
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });

  useEffect(() => {
    updateCurrentErrorValidationSegment('createList');
    dispatch(resetSnackbar());
    dispatch(getPaginatedLists({ force: true }));
  }, []);

  useEffect(() => {
    if (showSnackbar) {
      setSnackBarInfo({
        show: true,
        message: snackbarMessage,
        severity: snackbarSeverity
      });
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [showSnackbar]);

  const getButtonsGroup = () => [
    {
      name: 'Add List',
      action: () => onClickAddList()
    }
  ];

  const onClickEdit = row => {
    dispatch(setCurrentList(row));
    history.push(`/lists/${row.id}/subscribers`);
  };

  const onClickDelete = listId => {
    dispatch(
      userMessageActions.setMessage({
        title: 'Delete List',
        severity: 'warning',
        instructions: `Are you sure you want to delete this List?`,
        buttons: [
          dispatchButton('Delete', deleteList(listId)),
          dismissButton('Cancel')
        ]
      })
    );
  };

  const onClickSegmentedList = listId => {
    history.push(`lists/${listId}/segmented-list`);
  };

  const onClickImportStatus = listId => {
    history.push(`lists/${listId}/import-status`);
  };

  const getActionButtonOptions = (row) => [
    {
      name: isTemplateReadOnly(row, gettingList)
        ? 'View List'
        : 'Edit List',
      action: ['ACTION', () => onClickEdit(row)],
      ...(isTemplateReadOnly(row, gettingList) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: "Tenant Default list can be edited only with 'ALL' property"
      })
    },
    {
      name: 'Import Status',
      action: ['ACTION', () => onClickImportStatus(row.id)]
    },
    ...((getEnvVar(
      'ADM_INCLUDE_LIST_MANAGEMENT_PHASE2' === 'true') && getEnvVar(
        'ADM_INCLUDE_LIST_MANAGEMENT_SEGMENT_LIST' === 'true')) ? [{
      name: 'Manage Segmented List',
      action: ['ACTION', () => onClickSegmentedList(row.id)]
    }] : []),
    {
      name: 'Delete List',
      action: ['ACTION', () => onClickDelete(row.id)],
      ...(isTemplateReadOnly(row, gettingList) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: "Tenant Default list can be edited only with 'ALL' property"
      })
    }
  ];

  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateListFilters({ filterTemplates: templates, pageNo: 0 }))
      },
    ],
    resetFilters: () => dispatch(resetListFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Lists',
    searchAction: (payload) => dispatch(updateListFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedLists({ force: true })}
      />
      <TableComponentV2
        isLoading={gettingList}
        tableTitles={listTableTitles}
        actionButtonOptions={(row) => getActionButtonOptions(row)}
        onDoubleClickRow={(row) => onClickEdit(row)}
        filteredRows={listsData}
        action={getPaginatedLists({ force: true })}
        filterValues={filters}
        filterAction={(payload) => dispatch(updateListFilters(payload))}
      />

      {openCreateListModal && (
        <AddListModal
          isOpen={openCreateListModal}
          closeCreateListModal={closeListModal}
          lists={lists}
        />
      )}
      {showSnackbar && (
        <UserMessageSnackbar
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
          onClose={() => dispatch(resetSnackbar())}
        />
      )}
    </div>
  );
};

export default Lists;
